import Link from 'next/link';
import { NextSeo } from 'next-seo';

import {
  DocsArticleLayout,
  DocsCallout,
  DocsCard,
  DocsCardGrid,
  DocsStep,
  DocsSteps,
  DocsTable,
} from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'overview', title: '1. What connects to what' },
  { id: 'slack', title: '2. Slack' },
  { id: 'teams', title: '3. Microsoft Teams' },
  { id: 'discord', title: '4. Discord' },
  { id: 'telegram', title: '5. Telegram' },
  { id: 'whatsapp', title: '6. WhatsApp' },
  { id: 'helpdesks', title: '7. Zendesk & Zoho Desk' },
  { id: 'notion', title: '8. Notion' },
  { id: 'operating', title: '9. Running a multi-channel bot' },
];

const ChannelIntegrationsPage = () => {
  return (
    <>
      <NextSeo
        title="Channel Integrations"
        description="Connect a Chat.co bot to Slack, Microsoft Teams, Discord, Telegram, WhatsApp, Zendesk, Zoho Desk and Notion — what each one needs and what changes once it's live."
        canonical="https://chat.co/docs/channel-integrations"
        openGraph={{
          url: 'https://chat.co/docs/channel-integrations',
          title: 'Channel Integrations | Chat.co',
          description:
            'Connect a bot to Slack, Teams, Discord, Telegram, WhatsApp, Zendesk, Zoho Desk and Notion.',
          type: 'article',
          article: {
            publishedTime: '2026-08-30T00:00:00Z',
            modifiedTime: '2026-08-30T00:00:00Z',
            section: 'Deploy',
            tags: ['slack', 'teams', 'discord', 'telegram', 'whatsapp', 'zendesk', 'integrations'],
          },
        }}
        twitter={{ cardType: 'summary_large_image' }}
      />

      <DocsArticleLayout
        category="Deploy &amp; Connect"
        categoryColor="text-purple-700"
        title="Channel integrations"
        description="Your agent, wherever the conversation already happens — chat platforms, messaging apps, and the helpdesk your team lives in."
        readingTime="11 min read"
        lastUpdated="August 2026"
        tableOfContents={tableOfContents}
        prevArticle={{
          title: 'Website integration',
          href: PageLink.docsWebsiteIntegration,
        }}
        nextArticle={{
          title: 'Voice lines & appointments',
          href: PageLink.docsVoiceLines,
        }}
        keyTakeaways={[
          'Integrations are configured per bot, from the bot’s Integrations page',
          'Slack, Zoho Desk and Notion connect through OAuth; the rest need credentials you create',
          'Every channel shares one knowledge base — connect the bot, not a copy of it',
          'Each connection has a test action; use it before telling anyone the channel is live',
          'An internal channel and a public channel should almost never be the same bot',
        ]}>
        <p className="lead">
          A widget on your website only reaches people who visit your website. These integrations
          put the same agent — same knowledge, same prompt, same citations — inside the tools your
          customers and colleagues are already in. Everything below is configured from the{' '}
          <strong>Integrations</strong> page in a bot&apos;s sidebar.
        </p>

        <section id="overview" className="scroll-mt-28">
          <h2>1. What connects to what</h2>

          <DocsTable
            headers={['Channel', 'Audience', 'How it connects']}
            rows={[
              [
                <strong key="1">Slack</strong>,
                'Internal teams',
                'OAuth — install the app into a workspace',
              ],
              [
                <strong key="2">Microsoft Teams</strong>,
                'Internal teams',
                'Azure bot registration credentials',
              ],
              [
                <strong key="3">Discord</strong>,
                'Communities',
                'Bot token from the Discord Developer Portal',
              ],
              [
                <strong key="4">Telegram</strong>,
                'Consumers, international',
                'Bot token from @BotFather',
              ],
              [
                <strong key="5">WhatsApp</strong>,
                'Consumers, support',
                'Meta WhatsApp Business API credentials',
              ],
              [
                <strong key="6">Zendesk</strong>,
                'Support teams',
                'Subdomain, admin email and API token',
              ],
              [<strong key="7">Zoho Desk</strong>, 'Support teams', 'OAuth'],
              [<strong key="8">Notion</strong>, 'Internal knowledge', 'OAuth'],
            ]}
          />

          <DocsCallout variant="tip" title="One bot, many channels — usually">
            All channels attached to a bot answer from that bot&apos;s single knowledge base, so
            improving the knowledge improves every channel at once. The exception is audience: if
            Slack should see internal material that WhatsApp must never see, those are two bots.
            There is no per-channel content filter, and there should not be one — separation by bot
            is the safe design.
          </DocsCallout>
        </section>

        <section id="slack" className="scroll-mt-28">
          <h2>2. Slack</h2>
          <p>
            The lowest-friction integration on the list, and the one most teams get value from
            first. Slack uses OAuth, so there are no credentials to create or paste.
          </p>

          <DocsSteps>
            <DocsStep title="Start the install from Integrations">
              <p>Open the bot&apos;s Integrations page and choose Slack.</p>
            </DocsStep>
            <DocsStep title="Approve in Slack">
              <p>
                Slack shows exactly what the app can do and asks you to pick a workspace. You need
                permission to install apps there — in many organisations that means a Slack admin
                does this step.
              </p>
            </DocsStep>
            <DocsStep title="Invite the bot into channels">
              <p>
                Installing does not put it anywhere. Invite it into the channels it should serve —
                start with one, not all of them.
              </p>
            </DocsStep>
            <DocsStep title="Ask it something">
              <p>
                Mention it with a question you know the answer to. If it answers from your knowledge
                base, the connection is real.
              </p>
            </DocsStep>
          </DocsSteps>

          <p>
            Connected workspaces are listed on the Integrations page, so you can see and revoke what
            is installed.
          </p>
        </section>

        <section id="teams" className="scroll-mt-28">
          <h2>3. Microsoft Teams</h2>
          <p>
            More involved than Slack, because Teams bots are registered in Azure rather than
            installed from a marketplace. You will need:
          </p>

          <DocsTable
            headers={['Field', 'Where it comes from']}
            rows={[
              [<strong key="a">Microsoft App ID</strong>, 'Your Azure bot registration'],
              [
                <strong key="b">App Password (client secret)</strong>,
                'Generated alongside the App ID',
              ],
              [
                <strong key="c">Tenant ID</strong>,
                'Optional — leave empty for a multi-tenant bot, set it to lock the bot to one organisation',
              ],
              [<strong key="d">Bot display name</strong>, 'Optional — what your colleagues see'],
            ]}
          />

          <p>
            A setup code flow is also available for connecting an already-registered bot. Once
            connected, use the connection&apos;s <strong>test</strong> action before announcing it —
            Teams misconfigurations tend to fail silently rather than loudly.
          </p>

          <DocsCallout variant="note" title="Tenant ID is an access-control decision">
            Leaving it empty makes the bot multi-tenant. For an internal assistant over internal
            knowledge, set it. It costs nothing and closes an avenue you almost certainly did not
            intend to leave open.
          </DocsCallout>
        </section>

        <section id="discord" className="scroll-mt-28">
          <h2>4. Discord</h2>
          <p>
            Create an application in the Discord Developer Portal, add a bot to it, and paste the
            bot token into the Discord card on the Integrations page. Once connected, the page lists
            the servers the bot is in, and you can disconnect or reconnect without recreating the
            application.
          </p>

          <DocsCallout variant="warning" title="A bot token is a full credential">
            Anyone holding it can act as your bot. Paste it once here, never into a shared document
            or a ticket, and regenerate it in the Developer Portal if it is ever exposed.
          </DocsCallout>
        </section>

        <section id="telegram" className="scroll-mt-28">
          <h2>5. Telegram</h2>
          <p>
            The quickest of the token-based integrations. Message <strong>@BotFather</strong> on
            Telegram, run <code>/newbot</code>, and it hands you a token that looks like{' '}
            <code>123456789:ABCdefGhIJKlmNOPQRstUVwxyz</code>. Paste it into the Telegram card and
            connect.
          </p>
          <p>
            Telegram is worth considering wherever your audience is international — it has reach in
            markets where WhatsApp does not, and unlike WhatsApp it needs no business verification.
          </p>
        </section>

        <section id="whatsapp" className="scroll-mt-28">
          <h2>6. WhatsApp</h2>
          <p>
            The heaviest setup here, because Meta requires a verified WhatsApp Business account
            before you can send anything. Budget real time for the Meta side; the Chat.co side is
            five fields.
          </p>

          <DocsTable
            headers={['Field', 'Where it comes from']}
            rows={[
              [<strong key="a">Permanent Access Token</strong>, 'Meta Developer Portal'],
              [<strong key="b">Phone Number ID</strong>, 'Your WhatsApp Business number in Meta'],
              [<strong key="c">Business Account ID</strong>, 'Meta Business Suite'],
              [<strong key="d">Display Phone Number</strong>, 'The number customers will see'],
              [
                <strong key="e">Webhook Verify Token</strong>,
                'A secret you choose, then enter identically in the Meta Developer Portal',
              ],
            ]}
          />

          <p>Broadly, the order is:</p>
          <ul>
            <li>Create a WhatsApp Business account in Meta Business Suite</li>
            <li>Create an app with the WhatsApp product in the Meta Developer Portal</li>
            <li>Generate a permanent access token and collect the IDs above</li>
            <li>Enter everything into the WhatsApp card here and connect</li>
            <li>Set the webhook URL and verify token on the Meta side to match</li>
          </ul>

          <DocsCallout variant="warning" title="The verify token must match exactly">
            The single most common WhatsApp failure. Meta rejects the webhook if the token you chose
            here and the one you entered in the Developer Portal differ by so much as whitespace,
            and the resulting error does not say so clearly.
          </DocsCallout>
        </section>

        <section id="helpdesks" className="scroll-mt-28">
          <h2>7. Zendesk &amp; Zoho Desk</h2>
          <p>
            These are a different shape from the chat channels. Rather than giving customers a new
            place to talk to your bot, they connect your bot to where your support team already
            works — so escalations arrive as tickets inside your existing queue, assignment rules
            and SLAs.
          </p>

          <h3>Zendesk</h3>
          <p>Three fields, all from your Zendesk instance:</p>
          <ul>
            <li>
              <strong>Subdomain</strong> — the <code>mycompany</code> in{' '}
              <code>mycompany.zendesk.com</code>
            </li>
            <li>
              <strong>Admin email</strong> — an admin account on that instance
            </li>
            <li>
              <strong>API token</strong> — created in Zendesk Admin Center
            </li>
          </ul>

          <h3>Zoho Desk</h3>
          <p>
            OAuth — authorise from the Integrations page and grant access. No tokens to copy. Zoho
            Desk tickets can be created directly from{' '}
            <Link href={PageLink.docsHumanHandoff}>handoff requests</Link>, which is the main reason
            to connect it.
          </p>

          <DocsCallout variant="tip" title="Connect a helpdesk before you scale traffic">
            Handoffs arriving by email work fine at ten a week and fall apart at a hundred. If you
            already run a helpdesk, wiring it up early costs nothing and saves a migration later.
          </DocsCallout>
        </section>

        <section id="notion" className="scroll-mt-28">
          <h2>8. Notion</h2>
          <p>
            Notion connects over OAuth and is about knowledge rather than conversation: it links the
            bot to Notion content your team maintains, so the answers track the workspace instead of
            a snapshot you uploaded once.
          </p>
          <p>
            Worth it when Notion is genuinely where your documentation lives. If your Notion is
            half-finished — as most are — fix that first; connecting it does not improve it.
          </p>
        </section>

        <section id="operating" className="scroll-mt-28">
          <h2>9. Running a multi-channel bot</h2>

          <h3>Connect one channel and let it settle</h3>
          <p>
            Each channel surfaces different failure modes. Slack users ask in fragments; WhatsApp
            users expect brevity; Discord users test the boundaries for entertainment. Learning that
            on one channel is manageable; learning it on five at once is not.
          </p>

          <h3>Test every connection before you announce it</h3>
          <p>
            Most of these cards expose a test action. Use it. A channel that looks connected but
            silently fails is worse than one that was never set up, because people stop asking.
          </p>

          <h3>Treat internal and external as different bots</h3>
          <p>
            The knowledge base is shared across every channel attached to a bot. There is no
            per-channel content filter. If a document should reach your staff and not your
            customers, it belongs in a separate bot — see{' '}
            <Link href={PageLink.docsMultiBotOrchestration}>managing multiple bots</Link>.
          </p>

          <h3>Watch the credentials</h3>
          <p>
            Tokens expire, get rotated, and get revoked when someone leaves. When a channel goes
            quiet, check the connection status on the Integrations page before assuming the bot
            broke.
          </p>

          <DocsCardGrid columns={2}>
            <DocsCard
              icon="tabler:browser"
              title="Website integration"
              href={PageLink.docsWebsiteIntegration}>
              The widget, the iframe and the hosted share link.
            </DocsCard>
            <DocsCard icon="tabler:phone" title="Voice lines" href={PageLink.docsVoiceLines}>
              The same agent, on a phone number.
            </DocsCard>
          </DocsCardGrid>
        </section>
      </DocsArticleLayout>
    </>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default ChannelIntegrationsPage;
